import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronRight, ArrowRight, Plus, Trash2, Info, Ban } from 'lucide-react';
import type { Task } from '../../types';
import { FolderOpen, CircleDot } from 'lucide-react';
import { updateTask, createTask, deleteTask, useSubtasks } from '../../hooks/useTasks';
import { useAppStore } from '../../store';
import { useContextMenu } from '../../hooks/useContextMenu';
import { queryClient } from '../../api/queryClient';

interface BacklogCardProps {
  task: Task;
  zoom: number;
  onMoveToTrack: (task: Task) => void;
}

export function BacklogCard({ task, zoom, onMoveToTrack }: BacklogCardProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const [pendingPos, setPendingPos] = useState<{ x: number; y: number } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const dragStart = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const didMove = useRef(false);
  const latestDragPos = useRef(dragPos);
  latestDragPos.current = dragPos;

  const subtasks = useSubtasks(task.id);
  const ctxMenu = useContextMenu();
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'backlog'] });
  const isProject = task.itemType === 'project';

  const handleMouseDownFixed = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      didMove.current = false;
      setIsDragging(true);
      const startX = task.backlogX ?? 0;
      const startY = task.backlogY ?? 0;
      dragStart.current = { x: e.clientX, y: e.clientY, startX, startY };

      const handleMouseMove = (me: MouseEvent) => {
        if (!dragStart.current) return;
        const dx = (me.clientX - dragStart.current.x) / zoom;
        const dy = (me.clientY - dragStart.current.y) / zoom;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didMove.current = true;
        const newX = Math.max(0, dragStart.current.startX + dx);
        const newY = Math.max(0, dragStart.current.startY + dy);
        latestDragPos.current = { x: newX, y: newY };
        setDragPos({ x: newX, y: newY });
      };

      const handleMouseUp = async () => {
        setIsDragging(false);
        const finalPos = latestDragPos.current;
        if (didMove.current && finalPos) {
          queryClient.setQueryData<Task[]>(
            ['tasks', spaceId, 'backlog'],
            (old) => old?.map(t => t.id === task.id
              ? { ...t, backlogX: finalPos.x, backlogY: finalPos.y }
              : t
            )
          );
          setPendingPos(finalPos);
          setDragPos(null);
          latestDragPos.current = null;
          dragStart.current = null;
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          await updateTask(task.id, { backlogX: finalPos.x, backlogY: finalPos.y });
          return;
        }
        if (!didMove.current) {
          useAppStore.getState().setSelectedTaskId(task.id);
        }
        setDragPos(null);
        latestDragPos.current = null;
        dragStart.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [task.id, task.backlogX, task.backlogY, zoom]
  );

  useEffect(() => {
    if (pendingPos !== null &&
      Math.abs((task.backlogX ?? 0) - pendingPos.x) < 2 &&
      Math.abs((task.backlogY ?? 0) - pendingPos.y) < 2) {
      setPendingPos(null);
    }
  }, [task.backlogX, task.backlogY, pendingPos]);

  useEffect(() => {
    if (pendingPos === null) return;
    const t = setTimeout(() => setPendingPos(null), 700);
    return () => clearTimeout(t);
  }, [pendingPos]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    ctxMenu.open(e.clientX, e.clientY, [
      { label: 'View details', icon: <Info size={13} />, onClick: () => useAppStore.getState().setSelectedTaskId(task.id) },
      { label: 'Move to track', icon: <ArrowRight size={13} />, onClick: () => onMoveToTrack(task) },
      {
        label: task.blocked ? 'Unblock' : 'Mark as blocked',
        icon: <Ban size={13} />,
        onClick: async () => { await updateTask(task.id, { blocked: !task.blocked }); invalidate(); },
      },
      {
        label: subtasks.length > 0 ? `Delete (${subtasks.length} subtask${subtasks.length > 1 ? 's' : ''})` : 'Delete',
        icon: <Trash2 size={13} />, danger: true,
        onClick: async () => {
          if (subtasks.length > 0 && !confirm(`Delete "${task.title}" and its ${subtasks.length} subtask(s)?`)) return;
          await deleteTask(task.id); invalidate();
        },
      },
    ]);
  }, [task, subtasks.length, onMoveToTrack, ctxMenu]);

  const handleAddSubtask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    await createTask(newTitle.trim(), task.spaceId, 'p2', 'backlog', { parentId: task.id });
    setNewTitle('');
    setShowAdd(false);
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['subtasks', task.id] });
  };

  const progress = useMemo(() => {
    if (subtasks.length === 0) return 0;
    const total = subtasks.reduce((sum, st) => sum + (st.status === 'done' ? 100 : st.trackProgress), 0);
    return Math.round(total / subtasks.length);
  }, [subtasks]);

  const displayX = dragPos?.x ?? pendingPos?.x ?? Math.max(0, task.backlogX ?? 100);
  const displayY = dragPos?.y ?? pendingPos?.y ?? Math.max(0, task.backlogY ?? 100);

  const priorityColor = task.priority === 'p1' ? 'var(--color-p1)' : task.priority === 'p2' ? 'var(--color-p2)' : 'var(--color-p3)';

  return (
    <div
      className="absolute group"
      onContextMenu={handleContextMenu}
      style={{ left: `${displayX}px`, top: `${displayY}px`, zIndex: isDragging ? 50 : 1 }}
    >
      <div className="relative w-64">
        <div
          onMouseDown={handleMouseDownFixed}
          className={`${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
          <div className={`surface-card overflow-hidden transition-[border-color,box-shadow,transform] duration-150
            ${isDragging ? 'border-accent shadow-xl shadow-accent/10 scale-[1.02]' : 'hover:border-border-default'}
          `}>
            {/* Priority color strip */}
            <div className="h-1 w-full" style={{ backgroundColor: priorityColor }} />

            {/* Content */}
            <div className="p-3">
              <div className="flex items-start gap-2.5">
                {isProject ? (
                  <FolderOpen size={16} className="shrink-0 mt-0.5" style={{ color: task.color ?? '#6366f1' }} />
                ) : (
                  <CircleDot size={16} className="shrink-0 text-accent mt-0.5" />
                )}
                <div className="min-w-0 flex-1">
                  <span className="text-body font-semibold text-text-primary leading-tight block truncate">
                    {task.title}
                  </span>
                  {isProject && (
                    <span className="text-label text-text-muted mt-1 block">Project</span>
                  )}
                  {!!task.blocked && (
                    <span className="text-label text-p1 bg-p1/10 px-1.5 py-0.5 rounded mt-1 inline-block">Blocked</span>
                  )}
                </div>
              </div>

              {/* Metadata */}
              {subtasks.length > 0 && (
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-border-subtle rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: progress === 100 ? 'var(--color-finish)' : 'var(--color-accent)' }} />
                  </div>
                  <span className="text-micro text-text-muted font-mono tabular-nums">{progress}%</span>
                  <span className="text-micro text-text-muted">{subtasks.length} sub</span>
                </div>
              )}
            </div>

            {/* Hover action bar */}
            <div
              onMouseDown={(e) => e.stopPropagation()}
              className="border-t border-border-subtle flex items-center justify-between px-3 py-2
              opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-bg-raised">
              <button onClick={() => onMoveToTrack(task)} className="text-micro font-semibold text-accent hover:text-accent-hover flex items-center gap-1.5">
                <ArrowRight size={12} /> Move to Track
              </button>
              <button onClick={() => useAppStore.getState().setSelectedTaskId(task.id)} className="text-micro font-medium text-text-muted hover:text-text-secondary flex items-center gap-1.5">
                <Info size={12} /> Details
              </button>
            </div>
          </div>
        </div>

        {/* Subtasks expandable */}
        {subtasks.length > 0 && (
          <div className="mt-1.5 px-1">
            <button className="flex items-center gap-1.5 text-micro text-text-muted hover:text-text-secondary" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {subtasks.length} subtask{subtasks.length > 1 ? 's' : ''}
            </button>
            {expanded && (
              <div className="mt-1.5 space-y-1">
                {subtasks.map((st) => (
                  <button key={st.id} onClick={() => useAppStore.getState().setSelectedTaskId(st.id)}
                    className="w-full surface-card flex items-center gap-2 px-3 py-2 hover:border-border-default transition-colors text-left">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${st.priority === 'p1' ? 'bg-p1' : st.priority === 'p2' ? 'bg-p2' : 'bg-p3'}`} />
                    <span className="text-caption text-text-primary flex-1 truncate">{st.title}</span>
                    <span className={`text-micro font-mono shrink-0 ${st.status === 'done' ? 'text-finish' : 'text-text-muted'}`}>
                      {st.status === 'done' ? 'done' : `${st.trackProgress}%`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add subtask */}
        <div className="mt-1 ml-1">
          {showAdd ? (
            <form onSubmit={handleAddSubtask} className="flex items-center gap-1">
              <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                onBlur={() => !newTitle && setShowAdd(false)} onKeyDown={(e) => e.key === 'Escape' && setShowAdd(false)}
                placeholder="Subtask..." maxLength={200}
                className="bg-bg-raised border border-border-default rounded-lg px-2 py-1 text-caption text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent w-40" />
            </form>
          ) : (
            <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 text-micro text-text-muted hover:text-accent transition-colors opacity-0 group-hover:opacity-100">
              <Plus size={11} /> subtask
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
